import React from 'react';
import { StyleSheet } from 'aphrodite/no-important';
import {
  ArticleContent,
  ArticleMainImage,
  ArticleTitle,
  BreadCrumbs,
  ContentWithoutSidebar,
  FullWidthPostMainContent,
  LARGE_SCREEN_SIZE,
  OpenWeb,
  PostContentWithAside,
  ShareButton,
  Slideshow,
  Sponsor,
  SwitchableShareMenu,
  Disqus,
  SPORTRADAR_NAMES,
  TEAM_ID,
  FANSIDED_COLLEGE_ID,
  SportradarFactory,
  KeepScroll,
} from 'mm-ui-components';
import {
  ArticleComponentDataProps,
  FiniteScrollArticleComponentDataProps,
  MainArticleComponentDataProps,
} from '../utils/articleComponent.utils';
import { createBlocksWidth } from '../postPage.util';
import { useFiniteScrollBrowserURLManagerRegistration } from '../../components/finiteScroll/FiniteScrollBrowserURLManager';
import { DisclaimersAtDynamicPosition } from '../../components/disclaimer/DisclaimersAtDynamicPosition';
import { SLOT_NAMES } from '../templates.const';

const defaultBlocksWidths = createBlocksWidth(740, 730, 640);

const overrideBlocksWidths = {
  image: createBlocksWidth(760, 600, 660),
};
const coverWidth = createBlocksWidth(1080, 730, 640);
const openWebMargins = {
  left: 5,
  right: 0,
};
const breadCrumbsMarginFactor = {
  largeMarginFactor: 1,
  mediumMarginFactor: 1.5,
  smallMarginFactor: 1,
};

export const OdesaArticleComponent: React.FunctionComponent<ArticleComponentDataProps> = props => {
  const {
    breadCrumbs,
    title,
    intro,
    by,
    updatedAt,
    updatedAtISO,
    showUpdatedAt,
    authors,
    shareConfig,
    cover,
    siteName,
    templateName,
    sponsor,
    slideshow,
    body,
    sideBar1Id,
    sideBar2Id,
    createdAt,
    createdAtISO,
    tags,
    md5hashPostCanonicalUrl,
    openWebId,
    sideBar2AdOnSuccess,
    sideBar1AdOnSuccess,
    mainArticleURL,
    disclaimerText,
    reviewDisclaimerText,
    shouldDisplayDisclaimerAtBottom,
    commercialTags,
    shouldShowHeroImage = true,
    resourceID,
    ownerUsername,
    disqusSource,
    useFormatMinutesHoursAgo,
    isMobileViewer,
  } = props;

  const { experiments, canonical, addMMPlayerPlaceholder = false, addOpenwebLiveblogPlaceholder = false, shouldAddKeepScroll = false } = props as MainArticleComponentDataProps;
  const { articleUrl, topic, vertical } = props as FiniteScrollArticleComponentDataProps;

  const url = mainArticleURL || canonical || articleUrl;
  const ref = useFiniteScrollBrowserURLManagerRegistration(url as string, title);
  const [bottomPlaceholder, setBottomPlaceholder] = React.useState('bottom-placeholder');

  React.useEffect(() => {
    if (sideBar1Id?.match(/-[0-9]/g)) {
      const index = sideBar1Id.split('-')[2];
      setBottomPlaceholder(`bottom-placeholder-${index}`);
    }
  }, [sideBar1Id]);

  return (
    <div ref={ref}>
      <ContentWithoutSidebar maxWidth={LARGE_SCREEN_SIZE}>
        <FullWidthPostMainContent>
          <header>
            <ArticleTitle
              title={title}
              by={by}
              createdAt={createdAt}
              createdAtISO={createdAtISO}
              updatedAt={updatedAt}
              updatedAtISO={updatedAtISO}
              showUpdatedAt={showUpdatedAt}
              authors={authors}
              intro={intro}
              useFormatMinutesHoursAgo={useFormatMinutesHoursAgo}
            >
              <ShareButton shareConfig={shareConfig} canonical={url} smallScreenOnly />
            </ArticleTitle>
            {shouldShowHeroImage && (
              <ArticleMainImage
                {...cover}
                siteName={siteName}
                templateName={templateName}
                defaultBlocksWidths={coverWidth}
                paddingOverride={{ largePadding: 1, mediumPadding: 0, smallPadding: 0 }}
              />
            )}
          </header>
          {sponsor ? <Sponsor {...sponsor} /> : null}
          <SwitchableShareMenu shareConfig={shareConfig} canonical={url} hideOnSmallScreen />
          <PostContentWithAside
            adId1={sideBar1Id}
            adId2={sideBar2Id}
            adOnSuccess1={sideBar1AdOnSuccess}
            adOnSuccess2={sideBar2AdOnSuccess}
            adType1={SLOT_NAMES.SIDE_BAR_1}
            adType2={SLOT_NAMES.SIDE_BAR_2}
            postAsideChildren={!isMobileViewer && canonical ? <ScheduleWidgets customVertical={vertical} customTopic={topic} /> : null}
          >
            <Slideshow {...slideshow} experiments={experiments} resourceID={resourceID} commercialTags={commercialTags} ownerUsername={ownerUsername} blockWidths={defaultBlocksWidths}>
              <DisclaimersAtDynamicPosition
                disclaimerText={disclaimerText}
                reviewDisclaimerText={reviewDisclaimerText}
                defaultBlocksWidths={defaultBlocksWidths}
                shouldDisplayDisclaimerAtBottom={shouldDisplayDisclaimerAtBottom}
              >
                <ArticleContent
                  body={body}
                  templateName={templateName}
                  siteName={siteName}
                  defaultBlocksWidths={defaultBlocksWidths}
                  overrideBlocksWidths={overrideBlocksWidths}
                  addMMPlayerPlaceholder={addMMPlayerPlaceholder}
                  addOpenwebLiveblogPlaceholder={addOpenwebLiveblogPlaceholder}
                  imageWidthSmall={360}
                  imageWidthMedium={720}
                  imageWidthLarge={720}
                  totalSlides={slideshow.totalSlides}
                  experiments={experiments}
                  commercialTags={commercialTags}
                  slidesLinks={slideshow.slidesLinks}
                  resourceID={resourceID}
                  ownerUsername={ownerUsername}
                  isMobileViewer={isMobileViewer}
                />
              </DisclaimersAtDynamicPosition>
            </Slideshow>
            {breadCrumbs ? (
              <BreadCrumbs
                {...breadCrumbs}
                marginFactor={breadCrumbsMarginFactor}
                isBottom
              />
            ) : null}
          </PostContentWithAside>
        </FullWidthPostMainContent>
        {(disqusSource && resourceID) && <Disqus resourceID={resourceID} canonical={canonical} disqusSource={disqusSource} />}
        {shouldAddKeepScroll && <KeepScroll isIconAnimated={experiments?.includes('keep-scrolling|animation')} />}
        <div id={bottomPlaceholder} className="bottom-placeholder" />
        {openWebId && md5hashPostCanonicalUrl
        && (
          <OpenWeb
            spotId={openWebId}
            postCanonicalUrl={url}
            tags={tags}
            largeScreenMarginFactors={openWebMargins}
            md5hashPostCanonicalUrl={md5hashPostCanonicalUrl}
          />
        )}
      </ContentWithoutSidebar>
    </div>
  );
};

const ScheduleWidgets = ({ customVertical, customTopic }: {customVertical?: string | null; customTopic?: string | null}) => {
  const styles = StyleSheet.create({
    topWidgetWrapper: {
      boxShadow: 'none',
      padding: '0',
      marginTop: '40px',
    },
    widgetWrapper: {
      boxShadow: 'none',
      padding: '0',
    },
  });

  const vertical = customVertical?.toLowerCase();
  const topic = customTopic?.toLowerCase().replace(/ /g, '-');

  if (!topic || !vertical || (vertical && !(vertical in TEAM_ID))) return null;

  if ((vertical === 'ncaaf' || vertical === 'ncaab')) {
    const teamFid = FANSIDED_COLLEGE_ID.ncaaf[topic] || null;
    const teamBid = FANSIDED_COLLEGE_ID.ncaab[topic] || null;
    const sportradarProps = [(teamBid && { teamUid: teamBid, team: 'Basketball' }), (teamFid && { teamUid: teamFid, team: 'Football' })].filter(Boolean);
    const names = [(teamBid && SPORTRADAR_NAMES.NCAAB_RECENT_GAMES), (teamFid && SPORTRADAR_NAMES.NCAAF_RECENT_GAMES)].filter(Boolean);

    return (
      <>
        {names.length ? names.map((name, index) => {
          return (
            <SportradarFactory
              key={`${name}-${index}`}
              name={name}
              sportradarProps={sportradarProps[index] as any}
              wrapperStyle={index === 0 ? styles.topWidgetWrapper : styles.widgetWrapper}
            />
          );
        }) : null}
      </>
    );
  }

  if (topic && !(topic in TEAM_ID[vertical])) return null;

  const teamUid = TEAM_ID[vertical][topic] || null;

  const formatLeague = `${vertical?.toUpperCase()}_RECENT_GAMES`;
  // @ts-ignore
  const sportradarName = vertical ? SPORTRADAR_NAMES[formatLeague] : null;

  return (
    <>
      {sportradarName && teamUid && (
      <SportradarFactory
        name={sportradarName}
        sportradarProps={{ teamUid }}
        wrapperStyle={styles.topWidgetWrapper}
      />
    )}
    </>
  );
};
